import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Briefcase, Calendar, CheckCircle2, Clock3, Eye, Filter, Percent, Users, X, XCircle } from "lucide-react";
import * as XLSX from "xlsx";
import Swal from "sweetalert2";

import "./Consolidation.css";
import { fetchDemandesRecrutementRequest } from "../../../../redux/actions/demandesRecrutementActions";
import { fetchCampagnesRequest } from "../../../../redux/actions/campagnesActions";
import { getAllUsersRequest } from "../../../../redux/actions/userActions";
import {
    prolongerCampagneRequest,
    relanceParticipationCampagneRequest,
} from "../../../../redux/actions/demandesActions";

const PAGE_SIZE = 10;
const STATUS = {
    WAITING: "En attente",
    APPROVED: "Approuvée",
    //IN_PROGRESS: "En cours",
    REJECTED: "Rejetée",
    //CANCELLED: "Annulée",
};

const normalize = (value) => String(value ?? "").trim().toLowerCase();
const getStatus = (demande) => demande?.statut || demande?.status || "—";
const getPoste = (demande) => demande?.posteNom || demande?.poste || "—";

const formatDate = (value) => {
    if (!value) return "—";
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? "—" : date.toLocaleDateString("fr-FR");
};

const statusClass = (status) => {
    if (status === STATUS.APPROVED || status === STATUS.IN_PROGRESS) return "st-treated";
    if (status === STATUS.REJECTED || status === STATUS.CANCELLED) return "st-rejected";
    if (status === STATUS.WAITING) return "st-pending";
    return "st-default";
};

const DetailField = ({ label, value }) => (
    <div className="detailField">
        <div className="detailFieldLabel">{label}</div>
        <div className="detailFieldValue">{value || "—"}</div>
    </div>
);

const Modal = ({ demande, campagneNom, onClose }) => {
    if (!demande) return null;
    const status = getStatus(demande);

    return (
        <div className="consModal" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
            <div className="consModalPanel">
                <div className="consModalHeader">
                    <div className="consModalTitle">Détails de la demande #{demande.id}</div>
                    <button className="iconClose" type="button" onClick={onClose} title="Fermer">
                        <X size={18} />
                    </button>
                </div>
                <div className="consModalBody">
                    <div className="demandeDetailWrap">
                        <div className="detailHero">
                            <div className="detailHeroLeft">
                                <div className="detailHeroAvatar"><Briefcase size={22} /></div>
                                <div>
                                    <div className="detailHeroName">{getPoste(demande)}</div>
                                    <div className="detailHeroSub">{campagneNom}</div>
                                </div>
                            </div>
                            <div className={`pill ${statusClass(status)}`}>{status}</div>
                        </div>
                        <div className="detailSection">
                            <div className="detailSectionTitle">Informations de recrutement</div>
                            <div className="detailSectionGrid">
                                <DetailField label="Campagne" value={campagneNom} />
                                <DetailField label="Poste" value={getPoste(demande)} />
                                <DetailField label="Profil recherché" value={demande.profilRecherche || demande.profileRecherche} />
                                <DetailField label="Demandeur" value={demande.demandeurNom} />
                                <DetailField label="Années d'expérience" value={demande.anneesExperience} />
                                <DetailField label="Type d'emploi" value={demande.typeEmploi} />
                                <DetailField label="Statut" value={status} />
                                <DetailField label="Date de demande" value={formatDate(demande.createdAt || demande.dateCreation)} />
                                <DetailField label="Date de validation" value={formatDate(demande.dateValidation)} />
                                <DetailField label="Validateur" value={demande.validateurNom} />
                                <DetailField label="Motif" value={demande.motif} />
                                <DetailField label="Description" value={demande.description} />
                            </div>
                        </div>
                    </div>
                </div>
                <div className="consModalFooter">
                    <button className="btnPager" type="button" onClick={onClose}>Fermer</button>
                </div>
            </div>
        </div>
    );
};

const SimpleModal = ({ open, title, children, footer, onClose }) => {
    if (!open) return null;
    return (
        <div className="consModal" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
            <div className="consModalPanel consModalPanel--compact">
                <div className="consModalHeader">
                    <div className="consModalTitle">{title}</div>
                    <button className="iconClose" type="button" onClick={onClose}><X size={18} /></button>
                </div>
                <div className="consModalBody">{children}</div>
                {footer && <div className="consModalFooter">{footer}</div>}
            </div>
        </div>
    );
};

export default function Consolidation() {
    const dispatch = useDispatch();
    const demandesState = useSelector((state) => state.demandesRecrutement || {});
    const campagnesState = useSelector((state) => state.campagnes || {});
    const demandes = demandesState.demandes || [];
    const campagnes = campagnesState.list || [];
    const users = useSelector((state) => state.user?.users || state.users?.users || []);

    const [campagneId, setCampagneId] = useState("");
    const [showFilters, setShowFilters] = useState(false);
    const [poste, setPoste] = useState("");
    const [demandeur, setDemandeur] = useState("");
    const [typeEmploi, setTypeEmploi] = useState("");
    const [statut, setStatut] = useState("");
    const [page, setPage] = useState(1);
    const [selectedDemande, setSelectedDemande] = useState(null);
    const [showCollaborateurs, setShowCollaborateurs] = useState(false);
    const [showParticipation, setShowParticipation] = useState(false);
    const [showProlonger, setShowProlonger] = useState(false);
    const [showRelancer, setShowRelancer] = useState(false);
    const [newEndDate, setNewEndDate] = useState("");
    const [prolongationComment, setProlongationComment] = useState("");

    useEffect(() => {
        dispatch(fetchDemandesRecrutementRequest());
        dispatch(fetchCampagnesRequest());
        dispatch(getAllUsersRequest());
    }, [dispatch]);

    const recrutementCampagnes = useMemo(() => {
        const linkedIds = new Set(demandes.map((demande) => String(demande.campagneId)));
        return campagnes.filter((campagne) => {
            const type = normalize(campagne.typeCampagne || campagne.type);
            return type.includes("recrut") || linkedIds.has(String(campagne.id));
        });
    }, [campagnes, demandes]);

    useEffect(() => {
        if (!campagneId && recrutementCampagnes.length) {
            setCampagneId(String(recrutementCampagnes[0].id));
        }
    }, [campagneId, recrutementCampagnes]);

    const selectedCampagne = recrutementCampagnes.find((campagne) => String(campagne.id) === campagneId);
    const campagneName = (demande) => demande?.campagneNom || campagnes.find(
        (campagne) => Number(campagne.id) === Number(demande?.campagneId)
    )?.nom || "—";

    const campagneDemandes = useMemo(() => {
        if (!campagneId) return demandes;
        return demandes.filter((demande) => String(demande.campagneId) === campagneId);
    }, [demandes, campagneId]);

    const filtered = useMemo(() => campagneDemandes.filter((demande) => {
        if (poste && !normalize(getPoste(demande)).includes(normalize(poste))) return false;
        if (demandeur && !normalize(demande.demandeurNom).includes(normalize(demandeur))) return false;
        if (typeEmploi && normalize(demande.typeEmploi) !== normalize(typeEmploi)) return false;
        return !statut || getStatus(demande) === statut;
    }), [campagneDemandes, poste, demandeur, typeEmploi, statut]);

    useEffect(() => setPage(1), [campagneId, poste, demandeur, typeEmploi, statut]);

    const counts = useMemo(() => ({
        total: campagneDemandes.length,
        pending: campagneDemandes.filter((demande) => getStatus(demande) === STATUS.WAITING).length,
        approved: campagneDemandes.filter((demande) => getStatus(demande) === STATUS.APPROVED).length,
        inProgress: campagneDemandes.filter((demande) => getStatus(demande) === STATUS.IN_PROGRESS).length,
        rejected: campagneDemandes.filter((demande) => getStatus(demande) === STATUS.REJECTED).length,
        cancelled: campagneDemandes.filter((demande) => getStatus(demande) === STATUS.CANCELLED).length,
    }), [campagneDemandes]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const safePage = Math.min(page, totalPages);
    const pageItems = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
    const resetFilters = () => {
        setPoste("");
        setDemandeur("");
        setTypeEmploi("");
        setStatut("");
    };
    const activeFilters = [poste, demandeur, typeEmploi, statut].filter(Boolean).length;
    const startDate = selectedCampagne?.dateDebut || selectedCampagne?.startDate;
    const endDate = selectedCampagne?.dateFin || selectedCampagne?.endDate;

    const participants = useMemo(() => {
        const uniques = new Map();
        campagneDemandes.forEach((demande) => {
            const key = demande.demandeurId ?? demande.demandeurNom;
            if (key != null && !uniques.has(String(key))) {
                uniques.set(String(key), {
                    id: demande.demandeurId,
                    nom: demande.demandeurNom || `Utilisateur #${demande.demandeurId}`,
                    demandes: 0,
                });
            }
            if (key != null) uniques.get(String(key)).demandes += 1;
        });
        return [...uniques.values()];
    }, [campagneDemandes]);

    const populationCible = useMemo(() => {
        if (!selectedCampagne) return [];
        const entiteIds = new Set((selectedCampagne.entiteIds || []).map(Number));
        const posteIds = new Set((selectedCampagne.posteIds || []).map(Number));
        const userIds = new Set((selectedCampagne.userIds || []).map(Number));
        const activationUserIds = new Set((selectedCampagne.activationUserIds || []).map(Number));
        const hasFilters = entiteIds.size || posteIds.size || userIds.size || selectedCampagne.managerLevelFilter;
        const mode = String(selectedCampagne.filterMode || "IN").toUpperCase();

        return users.filter((user) => {
            if (user.isActive === false || String(user.status || "").toUpperCase() === "INACTIVE") return false;
            if (activationUserIds.size && !activationUserIds.has(Number(user.id))) return false;
            if (!hasFilters) return true;

            const entiteId = Number(user.entiteId ?? user.entityId ?? user.entity?.id);
            const posteId = Number(user.posteId ?? user.jobId ?? user.job?.id);
            const hasManager = Boolean(user.managerId ?? user.manager?.id);
            const managerMatch = selectedCampagne.managerLevelFilter ? hasManager : false;
            const matches = entiteIds.has(entiteId) || posteIds.has(posteId) || userIds.has(Number(user.id)) || managerMatch;
            return mode === "NOTIN" ? !matches : matches;
        });
    }, [selectedCampagne, users]);

    const population = populationCible.length;
    const participationRate = population > 0
        ? Math.min(100, Math.round((participants.length / population) * 100))
        : 0;

    const exportBilan = () => {
        if (!campagneDemandes.length) {
            Swal.fire({ icon: "info", text: "Aucune demande à exporter pour cette campagne." });
            return;
        }
        const rows = campagneDemandes.map((demande) => ({
            "N° demande": demande.id,
            Campagne: campagneName(demande),
            "Intitulé du poste": getPoste(demande),
            Demandeur: demande.demandeurNom || "—",
            "Profil recherché": demande.profilRecherche || demande.profileRecherche || "—",
            "Années d'expérience": demande.anneesExperience || "—",
            "Type d'emploi": demande.typeEmploi || "—",
            Statut: getStatus(demande),
            "Date de demande": formatDate(demande.createdAt || demande.dateCreation),
            "Date de validation": formatDate(demande.dateValidation),
            Validateur: demande.validateurNom || "—",
            Motif: demande.motif || "—",
        }));
        const worksheet = XLSX.utils.json_to_sheet(rows);
        worksheet["!cols"] = [12, 26, 28, 24, 30, 22, 18, 16, 18, 18, 24, 30].map((wch) => ({ wch }));
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Bilan recrutement");
        const safeName = normalize(selectedCampagne?.nom || "toutes-campagnes").replace(/[^a-z0-9]+/g, "-");
        XLSX.writeFile(workbook, `bilan-recrutement-${safeName}.xlsx`);
    };

    const confirmProlongation = () => {
        if (!campagneId || !newEndDate) return;
        dispatch(prolongerCampagneRequest({
            campagneId: Number(campagneId),
            newEndDate,
            comment: prolongationComment,
            onSuccess: () => {
                setShowProlonger(false);
                setNewEndDate("");
                setProlongationComment("");
                dispatch(fetchCampagnesRequest());
            },
        }));
    };

    const confirmRelance = () => {
        if (!campagneId) return;
        dispatch(relanceParticipationCampagneRequest({
            campagneId: Number(campagneId),
            onSuccess: () => setShowRelancer(false),
        }));
        setShowRelancer(false);
    };

    return (
        <div className="consPage">
            <h2>Consolidation recrutement</h2>
            <div className="consRow0">
                <div className="consCampagneSelect">
                    <select value={campagneId} onChange={(event) => setCampagneId(event.target.value)}>
                        <option value="">Toutes les campagnes</option>
                        {recrutementCampagnes.map((campagne) => (
                            <option key={campagne.id} value={campagne.id}>{campagne.nom || campagne.name}</option>
                        ))}
                    </select>
                </div>
                <div className="consRightPanel">
                    <div className="consRightRow">
                        <div className="consRightLabel">Collaborateurs concernés</div>
                        <button className="chipBtn" type="button" onClick={() => setShowCollaborateurs(true)}>
                            <Users size={15} /> {populationCible.length} COLLABORATEUR(S)
                        </button>
                    </div>
                    <div className="consRightRow">
                        <div className="consRightLabel">Taux de participation</div>
                        <button className={`rateBadge ${participationRate < 30 ? "is-low" : participationRate < 70 ? "is-mid" : "is-high"}`} type="button" onClick={() => setShowParticipation(true)}>
                            {participationRate}%
                        </button>
                    </div>
                </div>
            </div>

            <div className="consTop">
                <div className="consRange">
                    <Calendar size={18} />
                    <span>Du :</span><input value={formatDate(startDate)} readOnly disabled />
                    <span className="toTxt">au :</span><input value={formatDate(endDate)} readOnly disabled />
                </div>
                <div className="consTopBtns">
                    <button className="btnTop btnDark" type="button" onClick={exportBilan}>Extraire le bilan</button>
                    <button className="btnTop btnRed" type="button" disabled={!campagneId} onClick={() => setShowProlonger(true)}>Prolonger</button>
                    <button className="btnTop btnBlue" type="button" disabled={!campagneId} onClick={() => setShowRelancer(true)}>Relancer</button>
                </div>
            </div>

            <div className="consStats">
                <button className="statItem statBtn" type="button" onClick={() => setStatut("")}>
                    <div className="statNum cYellow">{counts.total}</div>
                    <div className="statTxt"><Users size={16} className="cYellow" />Total des demandes</div>
                </button>
                <button className="statItem statBtn" type="button" onClick={() => setStatut(STATUS.WAITING)}>
                    <div className="statNum cBlue">{counts.pending}</div>
                    <div className="statTxt"><Clock3 size={16} className="cBlue" />Demandes en attente</div>
                </button>
                <button className="statItem statBtn" type="button" onClick={() => setStatut(STATUS.APPROVED)}>
                    <div className="statNum cGreen">{counts.approved}</div>
                    <div className="statTxt"><CheckCircle2 size={16} className="cGreen" />Demandes approuvées</div>
                </button>
                <button className="statItem statBtn" type="button" onClick={() => setStatut(STATUS.REJECTED)}>
                    <div className="statNum cRed">{counts.rejected}</div>
                    <div className="statTxt"><XCircle size={16} className="cRed" />Demandes rejetées</div>
                </button>
            </div>

            <div className="recrutementFiltersToggle">
                <button
                    className={`recrutementFilterButton ${showFilters ? "isOpen" : ""}`}
                    type="button"
                    onClick={() => setShowFilters((value) => !value)}
                    aria-expanded={showFilters}
                >
                    <Filter size={17} />
                    <span>{showFilters ? "Masquer les filtres de recherche" : "Afficher les filtres de recherche"}</span>
                    {activeFilters > 0 && <span className="recrutementFilterCount">{activeFilters}</span>}
                </button>
            </div>

            {showFilters && (
                <div className="recrutementFiltersBox">
                    <div className="recrutementFiltersHeader">
                        <div>
                            <span className="recrutementFiltersIcon"><Filter size={16} /></span>
                            <div>
                                <h3>Filtres de recherche</h3>
                                <p>Affinez la liste des demandes de recrutement</p>
                            </div>
                        </div>
                        {activeFilters > 0 && <span className="recrutementActiveLabel">{activeFilters} actif{activeFilters > 1 ? "s" : ""}</span>}
                    </div>
                    <div className="filtersGrid">
                        <div className="fField"><label>Intitulé du poste</label><input value={poste} onChange={(event) => setPoste(event.target.value)} /></div>
                        <div className="fField"><label>Demandeur</label><input value={demandeur} onChange={(event) => setDemandeur(event.target.value)} /></div>
                        <div className="fField"><label>Type d'emploi</label><input value={typeEmploi} onChange={(event) => setTypeEmploi(event.target.value)} /></div>
                        <div className="fField"><label>Statut</label><select value={statut} onChange={(event) => setStatut(event.target.value)}><option value="">Tous</option>{Object.values(STATUS).map((value) => <option key={value} value={value}>{value}</option>)}</select></div>
                    </div>
                    <div className="filtersReset"><button className="btnReset" type="button" onClick={resetFilters}>Réinitialiser les valeurs</button></div>
                </div>
            )}

            <div className="consTableWrap">
                <table className="consTable">
                    <thead><tr><th>Intitulé du poste</th><th>Demandeur</th><th>Expérience</th><th>Type d'emploi</th><th className="center">Statut</th><th className="center">Actions</th></tr></thead>
                    <tbody>
                        {demandesState.loading ? <tr><td colSpan={6} className="emptyCell">Chargement...</td></tr> : pageItems.length === 0 ? <tr><td colSpan={6} className="emptyCell">Aucune demande de recrutement.</td></tr> : pageItems.map((demande) => {
                            const status = getStatus(demande);
                            return <tr key={demande.id}><td>{getPoste(demande)}</td><td>{demande.demandeurNom || "—"}</td><td>{demande.anneesExperience || "—"}</td><td>{demande.typeEmploi || "—"}</td><td className={`center ${statusClass(status)}`}>{status}</td><td className="center"><button className="eyeBtn" type="button" onClick={() => setSelectedDemande(demande)} title="Afficher"><Eye size={18} /></button></td></tr>;
                        })}
                    </tbody>
                </table>
                <div className="pager"><button className="btnPager" disabled={safePage <= 1} onClick={() => setPage((value) => value - 1)}>Précédent</button><div className="pagerInfo">Page <b>{safePage}</b> / {totalPages} — <span className="muted">{filtered.length} demandes</span></div><button className="btnPager" disabled={safePage >= totalPages} onClick={() => setPage((value) => value + 1)}>Suivant</button></div>
            </div>

            <Modal demande={selectedDemande} campagneNom={campagneName(selectedDemande)} onClose={() => setSelectedDemande(null)} />

            <SimpleModal open={showCollaborateurs} title="Collaborateurs concernés" onClose={() => setShowCollaborateurs(false)} footer={<button className="btnPager" onClick={() => setShowCollaborateurs(false)}>Fermer</button>}>
                <div className="modalSummary">Campagne : <b>{selectedCampagne?.nom || "—"}</b> — Population cible : <b>{populationCible.length}</b></div>
                <table className="miniTable"><thead><tr><th>Collaborateur</th><th>Email</th><th>Participation</th></tr></thead><tbody>{populationCible.length ? populationCible.map((user) => { const hasParticipated = participants.some((item) => Number(item.id) === Number(user.id)); const nom = `${user.firstName || user.prenom || ""} ${user.lastName || user.nom || ""}`.trim() || `Utilisateur #${user.id}`; return <tr key={user.id}><td>{nom}</td><td>{user.email || "—"}</td><td className={hasParticipated ? "st-treated" : "st-pending"}>{hasParticipated ? "A participé" : "En attente"}</td></tr>; }) : <tr><td colSpan={3} className="emptyCell">Aucune population cible définie.</td></tr>}</tbody></table>
            </SimpleModal>

            <SimpleModal open={showParticipation} title="Taux de participation" onClose={() => setShowParticipation(false)} footer={<button className="btnPager" onClick={() => setShowParticipation(false)}>Fermer</button>}>
                <div className="participationCard"><Percent size={24} /><div><strong>{participationRate}%</strong><span>{participants.length} participant(s) sur une population cible de {population} collaborateur(s)</span></div></div>
            </SimpleModal>

            <SimpleModal open={showProlonger} title="Prolonger la campagne" onClose={() => setShowProlonger(false)} footer={<><button className="btnPager" onClick={() => setShowProlonger(false)}>Annuler</button><button className="btnTop btnRed" disabled={!newEndDate} onClick={confirmProlongation}>Confirmer</button></>}>
                <div className="modalForm"><label>Nouvelle date de fin *</label><input type="date" min={new Date().toISOString().slice(0, 10)} value={newEndDate} onChange={(event) => setNewEndDate(event.target.value)} /><label>Commentaire</label><textarea rows={4} value={prolongationComment} onChange={(event) => setProlongationComment(event.target.value)} /></div>
            </SimpleModal>

            <SimpleModal open={showRelancer} title="Relancer les participants" onClose={() => setShowRelancer(false)} footer={<><button className="btnPager" onClick={() => setShowRelancer(false)}>Annuler</button><button className="btnTop btnBlue" onClick={confirmRelance}>Envoyer la relance</button></>}>
                <p>Cette action enverra une relance aux personnes concernées qui n'ont pas encore participé à la campagne de recrutement.</p>
            </SimpleModal>
        </div>
    );
}
